
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/Layout';
import { AuthService, AuthUser } from './services/authService';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { SetPassword } from './pages/SetPassword';
import { Loader2 } from 'lucide-react';
import { ErrorBoundary } from './components/ErrorBoundary';

// Lazy load pages
const Dashboard = lazy(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const CaptureInvoice = lazy(() => import('./pages/CaptureInvoice').then(module => ({ default: module.CaptureInvoice })));
const ReportDetail = lazy(() => import('./pages/ReportDetail').then(module => ({ default: module.ReportDetail })));
const ReportsList = lazy(() => import('./pages/ReportsList').then(module => ({ default: module.ReportsList })));
const InvoiceList = lazy(() => import('./pages/InvoiceList').then(module => ({ default: module.InvoiceList })));
const InvoiceDetail = lazy(() => import('./pages/InvoiceDetail').then(module => ({ default: module.InvoiceDetail })));
const Settings        = lazy(() => import('./pages/Settings').then(module => ({ default: module.Settings })));

interface AuthenticatedAppProps {
  user: AuthUser;
  onLogout: () => void;
  onUserUpdate: (user: AuthUser) => void;
}

const AuthenticatedApp: React.FC<AuthenticatedAppProps> = ({ user, onLogout, onUserUpdate }) => (
  <Layout onLogout={onLogout} user={user} isAdmin={user.role === 'admin'}>
    <ErrorBoundary>
      <Suspense fallback={
        <div className="flex items-center justify-center h-full min-h-[400px]">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      }>
        <Routes>
          <Route path="/" element={<Dashboard user={user} />} />
          <Route path="/capture" element={<CaptureInvoice user={user} />} />
          <Route path="/reports" element={<ReportsList />} />
          <Route path="/reports/:id" element={<ReportDetail />} />
          <Route path="/invoices" element={<InvoiceList />} />
          <Route path="/invoices/:id" element={<InvoiceDetail />} />
          <Route path="/settings" element={<Settings user={user} onUserUpdate={onUserUpdate} />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  </Layout>
);

const App: React.FC = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReset, setShowReset] = useState(false);

  useEffect(() => {
    // Safety timeout: if loading takes too long, show reset option
    const timer = setTimeout(() => {
      if (loading) setShowReset(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    // Check for existing session
    AuthService.getCurrentUser()
      .then((currentUser) => {
        setUser(currentUser);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading session:', err);
        setLoading(false);
      });

    // Listen for auth changes
    const { data: { subscription } } = AuthService.onAuthStateChange((currentUser) => {
      setUser(currentUser);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleLogout = async () => {
    await AuthService.signOut();
    setUser(null);
  };

  const handleHardReset = () => {
    if (window.confirm('Esto cerrará tu sesión y limpiará todos los datos locales para corregir errores. ¿Continuar?')) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-50">
        <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
        <p className="text-neutral-500 font-medium">Cargando...</p>
        {showReset && (
          <button
            onClick={handleHardReset}
            className="mt-8 text-sm text-danger hover:underline font-medium"
          >
            ¿Problemas cargando? Reiniciar App
          </button>
        )}
      </div>
    );
  }

  return (
    <HashRouter>
      <Routes>
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<SetPassword mode="reset" onSignedIn={setUser} />} />
        <Route path="/accept-invitation/:token" element={<SetPassword mode="invite" onSignedIn={setUser} />} />
        <Route
          path="/*"
          element={
            user
              ? <AuthenticatedApp user={user} onLogout={handleLogout} onUserUpdate={setUser} />
              : <Login onSignedIn={setUser} />
          }
        />
      </Routes>
    </HashRouter>
  );
};

export default App;
